import React, { useContext } from 'react';
import Text from '@amzn/meridian/text';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import userTokens from '@amzn/meridian-tokens/base/icon/user';
import Icon from '@amzn/meridian/icon';
import { EditableLabel } from './EditableLabel';
import { ShiftAllocationContext } from '../context/ShiftAllocationContext';
import { SCAN_ARMED_OUTLINE, SCAN_ARMED_GLOW } from '../../utils/constants';

const MAX_HEIGHT_FOR_PLACEHOLDER = 10;

export const EmptyEmployeeIcon = ({
  header = null,
  containerType,
  boardEntry,
  processPathId,
  minUnassignedAisleNo,
  maxUnassignedAisleNo,
  droppableId,
  placeHolder,
  editableHeader = false,
  onEditHeader
}) => {
  const { armedScanSlot, setArmedScanSlot } = useContext(ShiftAllocationContext) || {};
  const isArmed = Boolean(armedScanSlot && armedScanSlot.droppableId === droppableId);

  // Click-to-arm: mark this empty slot as the target for the next badge scan. Clicking the armed
  // slot again disarms it. The process comes from the caller rather than being parsed back out of
  // droppableId: the caller owns that string's layout, and a silently wrong process here would reach
  // computeSlotViolations and the assign dispatch.
  const onArm = (e) => {
    e.stopPropagation();
    if (!setArmedScanSlot) {
      return;
    }
    if (isArmed) {
      setArmedScanSlot(null);
      return;
    }
    setArmedScanSlot({
      droppableId,
      boardEntry,
      process: processPathId,
      minAisle: minUnassignedAisleNo != null ? minUnassignedAisleNo : null,
      maxAisle: maxUnassignedAisleNo != null ? maxUnassignedAisleNo : null,
      header
    });
  };

  // Keyboard equivalent of the click, so the slot is operable without a mouse.
  const onArmKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onArm(e);
    }
  };

  return (
    <Column spacing="none" width={'60px'} id={`${containerType}-${droppableId}`}>
      <div id={'placeholder-for-cluster'} style={{ maxHeight: MAX_HEIGHT_FOR_PLACEHOLDER }}>
        {placeHolder}
      </div>
      <Row spacing="none" alignmentHorizontal="center" width={'fit'}>
        {editableHeader ? (
          <EditableLabel value={header || ''} editable={true} onSave={onEditHeader} textType="B100" />
        ) : (
          header && (
            <Text transform={'capitalize'} type="B100" truncate={'true'}>
              {header}
            </Text>
          )
        )}
      </Row>
      <Row spacing="none">
        <div
          className={'labor-board-employee-icon-empty-div'}
          onClick={onArm}
          onKeyDown={onArmKeyDown}
          data-scan-target="true"
          role="button"
          tabIndex={0}
          aria-pressed={isArmed}
          title={isArmed ? 'Waiting for scan… click to cancel' : 'Click, then scan a badge to assign here'}
          style={{
            cursor: 'pointer',
            borderRadius: '50%',
            outline: isArmed ? SCAN_ARMED_OUTLINE : 'none',
            boxShadow: isArmed ? SCAN_ARMED_GLOW : 'none'
          }}>
          <Text>
            <Icon tokens={userTokens} className={'labor-board-employee-icon-empty'} />
          </Text>
        </div>
      </Row>
      {/* armed hint / spacer to align with non-empty icons */}
      <Row spacing="none" alignmentHorizontal="center" width={'fit'}>
        {isArmed ? (
          <Text type="b100" color="link" truncate={'true'}>
            Scan…
          </Text>
        ) : (
          <Text transform={'capitalize'} type="b100" truncate={'true'}>
            &nbsp;
          </Text>
        )}
      </Row>
      <div id={'placeholder-for-cluster'} style={{ maxHeight: MAX_HEIGHT_FOR_PLACEHOLDER }}>
        {placeHolder}
      </div>
    </Column>
  );
};
